import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { MapsProvider } from './contexts/MapsContext';
import { EventsProvider } from './contexts/EventsContext';
import Header from './components/common/Header';
import LoginButton from './components/auth/LoginButton';
import ConnectionStatus from './components/common/ConnectionStatus';
import StatsCards from './components/dashboard/StatsCards';
import MyMaps from './components/dashboard/MyMaps';
import MapCreationModal from './components/maps/MapCreationModal';
import PublicMapViewer from './components/public/PublicMapViewer';
import AdminDashboard from './components/admin/AdminDashboard';
import AdminUserDetails from './components/admin/AdminUserDetails';
import AnalyticsDashboard from './components/dashboard/AnalyticsDashboard/AnalyticsDashboard';
// import EventAnalyticsDashboard from './components/dashboard/EventAnalyticsDashboard';
import { AdminRoute } from './components/common/AdminRoute';
import { Map, QrCode, BarChart2 } from 'lucide-react';
import './App.css';

// ── Landing page logo (inline SVG pin using brand blue, no text) ──
// Replace the src below with your actual logo asset path, e.g. '/logo.png'
const LOGO_SRC = '/logo.png';

const AppContent = () => {
  const { isAuthenticated, loading, user, apiConnected } = useAuth();
  const [showCreateModal, setShowCreateModal] = useState(false);

  const handleMapCreated = (newMap) => {
    console.log('✅ New map created:', newMap);
    setShowCreateModal(false);
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="app-container">
        <div className="app-loading-container">
          <div className="app-spinner" />
          <p className="app-loading-text">Loading Pop Map…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Header />
      <main className="app-main">
        <ConnectionStatus />

        {isAuthenticated ? (
          <MapsProvider>
            <EventsProvider>
              <div className="app-dashboard">
                <div className="app-welcome-section">
                  <h2 className="app-welcome-title">Welcome back, {user.name}</h2>
                  <p className="app-welcome-subtitle">Manage your interactive event maps</p>
                </div>

                <div className="app-dashboard-grid">
                  <div className="app-action-card">
                    <div className="app-card-icon">
                      <Map size={20} />
                    </div>
                    <h3 className="app-card-title">Create New Map</h3>
                    <p className="app-card-text">
                      Design interactive maps for your events with custom markers and areas.
                    </p>
                    <button
                      className="app-primary-btn"
                      disabled={!apiConnected}
                      onClick={() => setShowCreateModal(true)}
                    >
                      Create Map
                    </button>
                  </div>
                </div>

                <StatsCards />
                <MyMaps />
              </div>
            </EventsProvider>
          </MapsProvider>
        ) : (
          <div className="app-landing">

            {/* Hero */}
            <div className="app-hero-section">
              <img
                src={LOGO_SRC}
                alt="Pop Map logo"
                className="app-hero-logo"
              />
              <h1 className="app-hero-title">
                Pop <span>Map</span>
              </h1>
              <p className="app-hero-subtitle">
                Interactive event maps — no app download needed
              </p>
              <p className="app-hero-tagline">Mapped for convenience</p>
            </div>

            {/* Login card */}
            <div className="app-login-section">
              <p className="app-login-heading">Get started</p>
              <p className="app-login-subheading">
                Sign in to create and manage your event maps
              </p>
              <LoginButton />
            </div>

            {/* Feature pills */}
            <div className="app-features-section">
              <p className="app-features-title">Everything you need</p>
              <div className="app-features-grid">
                <div className="app-feature-card">
                  <div className="app-feature-icon-wrap">
                    <Map size={18} />
                  </div>
                  <h4 className="app-feature-title">Quick Setup</h4>
                  <p className="app-feature-text">
                    Create maps in minutes with drag-and-drop marker placement.
                  </p>
                </div>
                <div className="app-feature-card">
                  <div className="app-feature-icon-wrap">
                    <QrCode size={18} />
                  </div>
                  <h4 className="app-feature-title">QR Sharing</h4>
                  <p className="app-feature-text">
                    One QR code gives attendees instant access — no installs.
                  </p>
                </div>
                <div className="app-feature-card">
                  <div className="app-feature-icon-wrap">
                    <BarChart2 size={18} />
                  </div>
                  <h4 className="app-feature-title">Live Analytics</h4>
                  <p className="app-feature-text">
                    See where people go, what they tap, and when they peak.
                  </p>
                </div>
              </div>
            </div>

          </div>
        )}
      </main>

      <footer className="app-footer">
        <div className="app-footer-content">
          <p className="app-footer-text">© 2025 Pop Map · Mapped for convenience</p>
        </div>
      </footer>

      <MapCreationModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onMapCreated={handleMapCreated}
      />
    </div>
  );
};

const NotFound = () => (
  <div className="app-container">
    <div className="app-loading-container">
      <h2 style={{ color: 'var(--text-primary)', marginBottom: '0.5rem' }}>Page not found</h2>
      <p style={{ color: 'var(--text-secondary)', marginBottom: '1.5rem' }}>
        The page you're looking for doesn't exist.
      </p>
      <a href="/" className="app-primary-btn" style={{ textDecoration: 'none' }}>
        Back to home
      </a>
    </div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/map/:shareCode" element={<PublicMapViewer />} />
          <Route path="/analytics/:mapId" element={<AnalyticsDashboard />} />
          {/* <Route path="/event-analytics/:eventId" element={<EventAnalyticsDashboard />} /> */}
          <Route
            path="/admin/users/:userId"
            element={<AdminRoute><AdminUserDetails /></AdminRoute>}
          />
          <Route
            path="/admin"
            element={<AdminRoute><AdminDashboard /></AdminRoute>}
          />
          <Route path="/" element={<AppContent />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;